/**
 * TypeScript types for the characters system.
 */

/**
 * Character stats - body part based attributes (1-100 percentage).
 */
export interface CharacterStats {
    head: number;   // Intelligence
    eyes: number;   // Wisdom
    mouth: number;  // Charisma
    arms: number;   // Strength
    body: number;   // Constitution
    legs: number;   // Dexterity
}

/**
 * Character skills (1-100 percentage).
 */
export interface CharacterSkills {
    combat: number;
    magic: number;
    crafting: number;
    healing: number;
    persuasion: number;
    stealth: number;
    observation: number;
    tea: number;
    building: number;
    herbalism: number;
    medicine: number;
    cooking: number;
}

/**
 * Stat with its label for display.
 * Includes base value, equipment bonus, and total value.
 */
export interface StatWithLabel {
    /** Total value (base + equipment) */
    value: number;
    /** Base character stat value */
    base: number;
    /** Bonus from equipped items */
    equipment: number;
    /** Human-readable label for the stat */
    label: string;
}

/**
 * Skill with its label and associated stat for display.
 * Includes base value, equipment bonus, and total value.
 */
export interface SkillWithLabel {
    /** Total value (base + equipment) */
    value: number;
    /** Base character skill value */
    base: number;
    /** Bonus from equipped items */
    equipment: number;
    /** Human-readable label for the skill */
    label: string;
    /** The associated stat key for this skill */
    stat: string | null;
}

/**
 * Character class information.
 */
export interface CharacterClassInfo {
    value: string;
    label: string;
    description: string;
}

/**
 * Character class with stat/skill ranges for character creation.
 */
export interface CharacterClassWithRanges extends CharacterClassInfo {
    statRanges: Record<string, [number, number]>;
    skillRanges: Record<string, [number, number]>;
}

/**
 * Represents a character page (wiki-style content).
 */
export interface CharacterPage {
    id: number;
    name: string;
    content: string | null;
    order: number;
}

/**
 * Represents a character media item (uploaded image).
 */
export interface CharacterMedia {
    id: number;
    original_filename: string;
    mime_type: string;
    file_size: number;
    formatted_size: string;
    width: number | null;
    height: number | null;
    alt_text: string | null;
    urls: {
        small: string;
        medium: string;
        large: string;
    };
    created_at: string;
}

/**
 * Inventory slot information for a character.
 */
export interface InventorySlotInfo {
    /** Total inventory slots available based on Body stat */
    total: number;
    /** Number of slots currently used (equipped items) */
    used: number;
    /** Number of available (free) slots */
    available: number;
}

/**
 * Character health information.
 */
export interface CharacterHealthInfo {
    /** Current health points */
    current: number;
    /** Maximum health points (Body × 2) */
    max: number;
    /** Health as a percentage (0-100) */
    percentage: number;
    /** Whether the character is incapacitated (0 HP) */
    is_incapacitated: boolean;
}

/**
 * Represents a character.
 */
export interface Character {
    id: number;
    name: string;
    /** Character pronouns (e.g., "he/him", "she/her", "they/them") */
    pronouns: string | null;
    class: CharacterClassInfo;
    stats: Record<string, StatWithLabel>;
    skills: Record<string, SkillWithLabel>;
    is_primary: boolean;
    hex_id: number;
    /** Total XP earned (cumulative, never resets) */
    xp: number;
    /** Current level (0-10) */
    level: number;
    /** XP required for next level (null if at max level) */
    xp_for_next_level: number | null;
    /** XP progress towards next level as percentage (0-100) */
    xp_progress: number;
    /** Whether the character can level up */
    can_level_up: boolean;
    /** Whether the character has pending points to assign */
    has_pending_points: boolean;
    /** Pending stat points to assign [2, 2, 3] or null */
    pending_stat_points: number[] | null;
    /** Pending skill points to assign [2, 2, 3] or null */
    pending_skill_points: number[] | null;
    /** Inventory slot information (total, used, available) */
    inventory_slots: InventorySlotInfo;
    /** Character health information */
    health: CharacterHealthInfo;
    /** Portrait image URL (medium size) */
    portrait_url: string | null;
    created_at: string | null;
}

/**
 * Response from the characters index endpoint.
 */
export interface CharactersIndexResponse {
    characters: Character[];
    hasPrimaryCharacter: boolean;
    classes: CharacterClassInfo[];
}

/**
 * Response from the classes endpoint.
 */
export interface CharacterClassesResponse {
    classes: CharacterClassWithRanges[];
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
    skillToStatMapping: Record<string, string>;
}

/**
 * Response from the character show endpoint (JSON API).
 */
export interface CharacterShowResponse {
    character: Character;
}

/**
 * Props for the Character/Show.tsx page (Inertia page).
 */
export interface CharacterDetailPageProps {
    character: Character;
    pages: CharacterPage[];
    selectedPage: CharacterPage | null;
    canEdit: boolean;
    hex: {
        id: number;
        name: string;
    };
    owner: {
        id: number;
        name: string;
        username: string | null;
    };
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
    /** Items equipped to this character */
    equipment: import('@/types/items').Item[];
    /** Completed adventure attempts this character participated in */
    completedAttempts: import('@/types/adventures').AdventureAttempt[];
    /** Whether the current user has blocked the character owner */
    isOwnerBlocked?: boolean;
    /** Building this character is assigned to, if any */
    assignedBuilding?: {
        id: number;
        hex_id: number;
        name: string | null;
        building_type: {
            name: string;
            slug: string;
        };
        slot: number;
    } | null;
}

/**
 * XP thresholds for each level (cumulative).
 * Level 0 = starting, Level 1 = 100 XP, etc.
 */
export const XP_THRESHOLDS: Record<number, number> = {
    0: 0,
    1: 100,
    2: 300,
    3: 700,
    4: 1500,
    5: 3100,
    6: 6300,
    7: 12700,
    8: 25500,
    9: 51100,
    10: 100000,
    11: 200000,
    12: 300000,
    13: 400000,
    14: 500000,
    15: 600000,
    16: 700000,
    17: 800000,
    18: 900000,
    19: 1000000,
    20: 2000000,
};

/** Maximum level a character can reach */
export const MAX_LEVEL = 20;

/** Maximum value for any stat or skill */
export const MAX_STAT_VALUE = 100;

/**
 * All stat keys.
 */
export const STAT_KEYS = ['head', 'eyes', 'mouth', 'arms', 'body', 'legs'] as const;
export type StatKey = typeof STAT_KEYS[number];

/**
 * All skill keys.
 */
export const SKILL_KEYS = [
    'combat', 'magic', 'crafting', 'healing', 'persuasion', 'stealth',
    'observation', 'tea', 'building', 'herbalism', 'medicine', 'cooking',
] as const;
export type SkillKey = typeof SKILL_KEYS[number];

/**
 * Skill to stat mapping.
 */
export const SKILL_TO_STAT: Record<SkillKey, StatKey> = {
    combat: 'arms',
    magic: 'head',
    crafting: 'arms',
    healing: 'eyes',
    persuasion: 'mouth',
    stealth: 'legs',
    observation: 'eyes',
    tea: 'mouth',
    building: 'body',
    herbalism: 'eyes',
    medicine: 'head',
    cooking: 'head',
};

/**
 * Human-readable stat labels.
 */
export const STAT_LABELS: Record<StatKey, string> = {
    head: 'Head (Intelligence)',
    eyes: 'Eyes (Wisdom)',
    mouth: 'Mouth (Charisma)',
    arms: 'Arms (Strength)',
    body: 'Body (Constitution)',
    legs: 'Legs (Dexterity)',
};

/**
 * Human-readable skill labels.
 */
export const SKILL_LABELS: Record<SkillKey, string> = {
    combat: 'Combat',
    magic: 'Magic',
    crafting: 'Crafting',
    healing: 'Healing',
    persuasion: 'Persuasion',
    stealth: 'Stealth',
    observation: 'Observation',
    tea: 'Tea',
    building: 'Building',
    herbalism: 'Herbalism',
    medicine: 'Medicine',
    cooking: 'Cooking',
};

/**
 * Get a color class based on the stat/skill value (percentage).
 */
export function getValueColorClass(value: number): string {
    if (value >= 40) return 'text-green-400';
    if (value >= 25) return 'text-blue-400';
    if (value >= 15) return 'text-yellow-400';
    return 'text-gray-400';
}

/**
 * Get a background color class for progress bars based on value.
 */
export function getProgressColorClass(value: number): string {
    if (value >= 40) return 'bg-green-500';
    if (value >= 25) return 'bg-blue-500';
    if (value >= 15) return 'bg-yellow-500';
    return 'bg-gray-500';
}

/**
 * Inventory slot thresholds based on Body stat.
 * Body 1-30 = 1 slot, 31-50 = 2 slots, 51-70 = 3 slots, 71-99 = 4 slots, 100+ = 5 slots.
 */
export const INVENTORY_SLOT_THRESHOLDS: { minBody: number; slots: number; label: string }[] = [
    { minBody: 1, slots: 1, label: '1-30' },
    { minBody: 31, slots: 2, label: '31-50' },
    { minBody: 51, slots: 3, label: '51-70' },
    { minBody: 71, slots: 4, label: '71-99' },
    { minBody: 100, slots: 5, label: '100+' },
];

/**
 * Get the number of inventory slots for a given Body stat value.
 */
export function getInventorySlotsForBody(bodyValue: number): number {
    let slots = 1;
    for (const threshold of INVENTORY_SLOT_THRESHOLDS) {
        if (bodyValue >= threshold.minBody) {
            slots = threshold.slots;
        }
    }
    return slots;
}

/**
 * Adventure speed bonus thresholds based on Legs stat.
 * Higher Legs stat reduces adventure completion time.
 * Legs 1-30 = 10% faster, 31-50 = 20% faster, 51-70 = 30% faster, 71-99 = 40% faster, 100+ = 50% faster.
 */
export const ADVENTURE_SPEED_THRESHOLDS: { minLegs: number; speedBonus: number; label: string }[] = [
    { minLegs: 1, speedBonus: 10, label: '1-30' },
    { minLegs: 31, speedBonus: 20, label: '31-50' },
    { minLegs: 51, speedBonus: 30, label: '51-70' },
    { minLegs: 71, speedBonus: 40, label: '71-99' },
    { minLegs: 100, speedBonus: 50, label: '100+' },
];

/**
 * Get the adventure speed bonus percentage for a given Legs stat value.
 * Returns the percentage time reduction (e.g., 20 means 20% faster = 80% of original time).
 */
export function getAdventureSpeedBonusForLegs(legsValue: number): number {
    let speedBonus = 10;
    for (const threshold of ADVENTURE_SPEED_THRESHOLDS) {
        if (legsValue >= threshold.minLegs) {
            speedBonus = threshold.speedBonus;
        }
    }
    return speedBonus;
}
